/* eslint-disable unicorn/no-array-reduce */
/* eslint-disable jsx-a11y/click-events-have-key-events */
import { useState } from 'react'
import { useSwipeable } from 'react-swipeable'
import type { BottegaBlock } from '@velasca/types'
import { sanitizeHTML } from '@velasca/utils/sanitize-html'

export interface BottegaLabels {
  label_read_more: string
  label_address: string
  label_hours: string
}

interface BottegaProps {
  labels: BottegaLabels
  bottega: BottegaBlock
}

interface BottegaBlockVisualBottom {
  visual_bottom_01: string
  visual_bottom_01_mobile: string
  visual_bottom_02: string
  visual_bottom_02_mobile: string
  visual_bottom_03: string
  visual_bottom_03_mobile: string
  visual_bottom_04: string
  visual_bottom_04_mobile: string
}

interface BottegaImage {
  desktop: (string | BottegaBlockVisualBottom)[]
  mobile: (string | BottegaBlockVisualBottom)[]
}

const Bottega = ({ labels, bottega }: BottegaProps) => {
  const [desktopSlide, setDesktopSlide] = useState<number>(0)
  const [mobileSlide, setMobileSlide] = useState<number>(0)

  const slides = [1, 2, 3, 4].reduce<BottegaImage>(
    (accumulator, current) => {
      const desktop =
        bottega[`visual_bottom_0${current}` as keyof BottegaBlockVisualBottom]

      const mobile =
        bottega[
          `visual_bottom_0${current}_mobile` as keyof BottegaBlockVisualBottom
        ]

      return {
        desktop: desktop
          ? [...accumulator.desktop, desktop]
          : accumulator.desktop,
        mobile: mobile ? [...accumulator.mobile, mobile] : accumulator.mobile,
      }
    },
    { desktop: [], mobile: [] },
  )

  const handlers = useSwipeable({
    onSwipedLeft: () => {
      setMobileSlide(
        mobileSlide + 1 < slides.mobile.length ? mobileSlide + 1 : mobileSlide,
      )
    },
    onSwipedRight: () => {
      setMobileSlide(mobileSlide - 1 >= 0 ? mobileSlide - 1 : mobileSlide)
    },
    preventScrollOnSwipe: true,
    trackMouse: false,
  })

  return (
    <div className="bottega fade-in">
      <div className="text-block">
        <div className="text-block-inner">
          <h3 dangerouslySetInnerHTML={sanitizeHTML(bottega.pretitle)}></h3>

          <p dangerouslySetInnerHTML={sanitizeHTML(bottega.mini_paragraph)}></p>

          {bottega.handle && (
            <a
              href={`/pages/${bottega.handle}`}
              dangerouslySetInnerHTML={sanitizeHTML(labels.label_read_more)}
            ></a>
          )}
        </div>

        <div className="info-block">
          <div className="inner-block">
            <h4>{labels.label_address}</h4>

            <p dangerouslySetInnerHTML={sanitizeHTML(bottega.address)}></p>

            {bottega.icon && (
              <img
                className="icon"
                src={bottega.icon}
                alt={`${bottega.title} | Velasca`}
              />
            )}
          </div>

          <div className="inner-block">
            <h4>{labels.label_hours}</h4>
            <div
              dangerouslySetInnerHTML={sanitizeHTML(bottega.hours_html)}
            ></div>
          </div>
        </div>
      </div>

      <div {...handlers} className={`image-block ${bottega.color_type ?? ''}`}>
        <div className="image-list">
          <div
            className="image desktop"
            style={{ transform: `translateX(${desktopSlide * -100}%)` }}
          >
            {slides.desktop.map((slide, i) => {
              if (slide) {
                return (
                  <img
                    key={`imagesDesktop-${i}`}
                    className={`desktop${desktopSlide === i ? ' active' : ''}`}
                    src={slide as keyof BottegaBlockVisualBottom}
                    alt={`${bottega.title?.replace(/&#39;/g, "'")} | Velasca`}
                  />
                )
              }

              return null
            })}
          </div>
          <div
            className="image mobile"
            style={{ transform: `translateX(${mobileSlide * -100}%)` }}
          >
            {slides.mobile.map((slide, i) => (
              <img
                key={`imagesMobile-${i}`}
                className={`mobile${mobileSlide === i ? ' active' : ''}`}
                src={slide as keyof BottegaBlockVisualBottom}
                alt={`${bottega.title?.replace(/&#39;/g, "'")} | Velasca`}
              />
            ))}
          </div>
        </div>
        <div className="selector-dots desktop">
          {slides.desktop.length > 1 &&
            slides.desktop.map((slide, i) => (
              <div
                className={`dot ${desktopSlide === i ? 'active' : ''}`}
                key={`dots-${i}`}
                onClick={() => {
                  setDesktopSlide(i)
                }}
              ></div>
            ))}
        </div>
        <div className="selector-dots mobile">
          {slides.mobile.length > 1 &&
            slides.desktop.map((slide, i) => (
              <div
                className={`dot ${mobileSlide === i ? 'active' : ''}`}
                key={`dots-${i}`}
                onClick={() => {
                  setMobileSlide(i)
                }}
              ></div>
            ))}
        </div>
      </div>
    </div>
  )
}

export default Bottega
